// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { GripVertical } from "lucide-react";
import type React from "react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { BenchmarkMetricCardProps } from "@/components/metrics/types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function BenchmarkMetricCard<T = Record<string, unknown>>({
  title,
  value,
  isLoading,
  onDrilldown,
  isWip,
  tooltip,
  category,
  benchmarkValue,
  benchmarkLoading,
  isDragging = false,
  isDraggingOverlay = false, // Used by DragOverlay to apply special styling
  attributes,
  listeners,
}: BenchmarkMetricCardProps<T>) {
  const categoryStyle = CATEGORY_STYLES[category];

  // Function to stop event propagation when clicking on the drag handle
  const handleStopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={
        isDragging
          ? "relative rounded-lg border-2 border-dashed border-muted-foreground/25 h-[180px] sm:h-[185px] md:h-[180px] lg:h-[175px] xl:h-[180px] bg-background"
          : ""
      }
    >
      <div className={isDragging ? "invisible" : ""}>
        <Card
          className={`group relative h-[180px] sm:h-[185px] md:h-[180px] lg:h-[175px] xl:h-[180px] overflow-hidden ${
            isDraggingOverlay
              ? "cursor-grabbing shadow-md opacity-90"
              : "hover:shadow-md transition-shadow duration-200"
          }`}
          onClick={onDrilldown}
        >
          <div className="cursor-pointer hover:bg-accent/30 h-full flex flex-col">
            <CardHeader className="pb-1 pt-4 px-4 flex-shrink-0">
              <div className="flex items-start gap-1.5">
                <div className="text-xs sm:text-[11px] md:text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide line-clamp-2 h-6 overflow-hidden leading-tight">
                  {title}
                  {tooltip && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="text-muted-foreground hover:text-foreground transition-colors inline-block ml-1 align-text-top"
                          aria-label={`Info about ${title}`}
                          onClick={handleStopPropagation}
                        >
                          <InfoCircledIcon className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="right"
                        className="z-50 w-72 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-none"
                        sideOffset={5}
                      >
                        <p className="text-sm">{tooltip}</p>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                {isWip && (
                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-medium shrink-0">
                    WIP
                  </span>
                )}
              </div>
            </CardHeader>

            <CardContent className="flex-grow px-4 pt-1 pb-4">
              <div className="h-full flex flex-col">
                {/* Top row: Badges for benchmark mode with responsive sizing */}
                <div className="flex justify-between mb-1.5">
                  <Badge
                    variant="default"
                    className="text-xs sm:text-[11px] md:text-[9px] lg:text-[8px] xl:text-xs px-1.5 py-0.5"
                  >
                    Campaign
                  </Badge>
                  <Badge
                    variant="default"
                    className="text-xs sm:text-[11px] md:text-[9px] lg:text-[8px] xl:text-xs px-1.5 py-0.5"
                  >
                    Benchmark
                  </Badge>
                </div>

                {/* Main values container - always horizontal with smart sizing */}
                <div className="flex-grow flex items-center justify-between gap-1 min-w-0">
                  {/* Campaign section */}
                  <div className="flex flex-col justify-center min-w-0 flex-1 max-w-[55%]">
                    {isLoading ? (
                      <div className="h-8 w-20 animate-pulse rounded bg-muted" />
                    ) : (
                      <span className="text-2xl sm:text-3xl md:text-xl lg:text-lg xl:text-3xl font-bold leading-tight truncate block">
                        {value}
                      </span>
                    )}
                  </div>

                  {/* Benchmark section */}
                  <div className="flex flex-col justify-center items-end min-w-0 flex-1 max-w-[45%]">
                    {benchmarkLoading ? (
                      <div className="h-6 w-16 animate-pulse rounded bg-muted ml-auto" />
                    ) : (
                      <span
                        className="text-xl sm:text-2xl md:text-lg lg:text-base xl:text-2xl font-bold leading-tight truncate text-right block w-full"
                        title={
                          typeof benchmarkValue === "string" ||
                          typeof benchmarkValue === "number"
                            ? String(benchmarkValue)
                            : undefined
                        }
                      >
                        {benchmarkValue}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>

            {/* Category strip at the bottom with text on right */}
            <div
              className={`absolute bottom-0 left-0 right-0 py-1.5 px-4 flex items-center justify-end ${categoryStyle.color}`}
            >
              <div className="flex items-center gap-1.5">
                {categoryStyle.icon}
                <span className="text-[10px] sm:text-[9px] md:text-[8px] lg:text-[10px] font-medium uppercase leading-none">
                  {category}
                </span>
              </div>
            </div>
          </div>

          {/* Drag handle for normal mode */}
          {attributes && listeners && !isDraggingOverlay && (
            <div
              {...attributes}
              {...listeners}
              onClick={handleStopPropagation}
              className={`absolute top-2 right-2 flex h-6 w-6 items-center justify-center text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              }`}
            >
              <GripVertical className="h-4 w-4" />
            </div>
          )}

          {/* Drag handle for overlay mode */}
          {isDraggingOverlay && (
            <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center text-muted-foreground cursor-grabbing">
              <GripVertical className="h-4 w-4" />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
